// zip_reader.js
//
// Finds the XML files inside a zip without reading the whole archive into memory. A Fiken
// "Komplett eksport" is one SAF-T file per fiscal year next to every voucher PDF the company
// ever received, so it can run to gigabytes while the XML we want is a few megabytes. We read
// the central directory from the end of the file, and then stream only the entries we need
// through the browser's own DecompressionStream.
//
// The accounting app's Saft::Adapter::ZipArchive does the same job on the server; this is the
// client-side half, because nothing the customer uploads on the pricing page may leave the
// browser.

const EOCD_SIGNATURE = 0x06054b50
const ZIP64_LOCATOR_SIGNATURE = 0x07064b50
const CENTRAL_SIGNATURE = 0x02014b50
const LOCAL_SIGNATURE = 0x04034b50
const MAX_32 = 0xffffffff

export class ZipError extends Error {}

export function isZip(file) {
  return /\.zip$/i.test(file.name || '') || /zip/.test(file.type || '')
}

// Every XML entry in the archive, without the resource forks macOS adds when zipping from
// Finder. Each entry streams its own decompressed bytes on demand.
export async function xmlEntries(file) {
  const entries = await centralDirectory(file)
  return entries
    .filter((entry) => /\.xml$/i.test(entry.name) && !/(^|\/)__MACOSX\//.test(entry.name))
    .map((entry) => ({ ...entry, stream: () => entryStream(file, entry) }))
}

async function bytes(file, start, end) {
  return new DataView(await file.slice(start, end).arrayBuffer())
}

async function centralDirectory(file) {
  // The end-of-central-directory record is 22 bytes plus a comment of at most 64 KiB.
  const tailStart = Math.max(0, file.size - 22 - 0xffff)
  const tail = await bytes(file, tailStart, file.size)
  let eocd = -1
  for (let i = tail.byteLength - 22; i >= 0; i--) {
    if (tail.getUint32(i, true) === EOCD_SIGNATURE) {
      eocd = i
      break
    }
  }
  if (eocd < 0) throw new ZipError('Filen ser ikke ut som en gyldig zip-fil.')

  let count = tail.getUint16(eocd + 10, true)
  let size = tail.getUint32(eocd + 12, true)
  let offset = tail.getUint32(eocd + 16, true)

  // Zip64: an archive over 4 GiB or with more than 65 535 entries keeps the real numbers in a
  // second record, pointed at by a locator just before the ordinary one.
  if (offset === MAX_32 || size === MAX_32 || count === 0xffff) {
    const locator = eocd - 20
    if (locator < 0 || tail.getUint32(locator, true) !== ZIP64_LOCATOR_SIGNATURE) {
      throw new ZipError('Zip-filen er for stor til å leses her. Pakk den ut og last opp XML-filen.')
    }
    const recordOffset = Number(tail.getBigUint64(locator + 8, true))
    const record = await bytes(file, recordOffset, recordOffset + 56)
    count = Number(record.getBigUint64(32, true))
    size = Number(record.getBigUint64(40, true))
    offset = Number(record.getBigUint64(48, true))
  }

  const directory = await bytes(file, offset, offset + size)
  const decoder = new TextDecoder()
  const entries = []
  let pos = 0
  for (let n = 0; n < count && pos + 46 <= directory.byteLength; n++) {
    if (directory.getUint32(pos, true) !== CENTRAL_SIGNATURE) break

    const method = directory.getUint16(pos + 10, true)
    let compressedSize = directory.getUint32(pos + 20, true)
    let size = directory.getUint32(pos + 24, true)
    const nameLength = directory.getUint16(pos + 28, true)
    const extraLength = directory.getUint16(pos + 30, true)
    const commentLength = directory.getUint16(pos + 32, true)
    let localOffset = directory.getUint32(pos + 42, true)
    const name = decoder.decode(new Uint8Array(directory.buffer, directory.byteOffset + pos + 46, nameLength))

    // The zip64 extra field holds, in this order, whichever of the three sizes overflowed.
    let extra = pos + 46 + nameLength
    const extraEnd = extra + extraLength
    while (extra + 4 <= extraEnd) {
      const id = directory.getUint16(extra, true)
      const length = directory.getUint16(extra + 2, true)
      if (id === 0x0001) {
        let field = extra + 4
        if (size === MAX_32) {
          size = Number(directory.getBigUint64(field, true))
          field += 8
        }
        if (compressedSize === MAX_32) {
          compressedSize = Number(directory.getBigUint64(field, true))
          field += 8
        }
        if (localOffset === MAX_32) localOffset = Number(directory.getBigUint64(field, true))
      }
      extra += 4 + length
    }

    entries.push({ name, method, compressedSize, size, localOffset })
    pos = extraEnd + commentLength
  }
  return entries
}

async function entryStream(file, entry) {
  const header = await bytes(file, entry.localOffset, entry.localOffset + 30)
  if (header.getUint32(0, true) !== LOCAL_SIGNATURE) {
    throw new ZipError('Zip-filen er skadet.')
  }
  const start = entry.localOffset + 30 + header.getUint16(26, true) + header.getUint16(28, true)
  const raw = file.slice(start, start + entry.compressedSize).stream()

  if (entry.method === 0) return raw
  if (entry.method !== 8) {
    throw new ZipError('Zip-filen bruker en komprimering vi ikke kan lese. Pakk den ut og last opp XML-filen.')
  }
  if (typeof DecompressionStream === 'undefined') {
    throw new ZipError('Nettleseren din kan ikke pakke ut zip-filer. Pakk den ut og last opp XML-filen.')
  }
  return raw.pipeThrough(new DecompressionStream('deflate-raw'))
}
